import { type ReactNode } from 'react';
import { createContext, useState, useMemo } from 'react';

import {
  type Network,
  type User,
  type Admin,
  type NetworkId,
} from 'hb-react/types';

type SetContextStateAction<T> = (value: Partial<T>) => void;

export const getCurrentUrl = () => {
  if (typeof window !== 'undefined') {
    return { origin: window.location.origin };
  }

  return {};
};

export const CurrentUserContext = createContext<{
  currentUser: User | null;
  updateCurrentUser: (user: Partial<User>) => void;
}>({
  currentUser: null as User | null,
  updateCurrentUser: () => {},
});

export const CurrentAdminContext = createContext<{
  currentAdmin: Admin | null;
  updateCurrentAdmin?: (admin: Partial<Admin>) => void;
}>({
  currentAdmin: null,
  updateCurrentAdmin: () => {},
});

export const CurrentNetworkContext = createContext<{
  currentNetwork: Network;
  currentNetworkId?: number;
  updateCurrentNetwork: SetContextStateAction<Network>;
}>({
  currentNetwork: {} as Network,
  currentNetworkId: undefined,
  updateCurrentNetwork: () => {},
});

export const CurrentUrlContext = createContext<{
  currentUrl: Partial<{ origin: string }>;
  updateCurrentUrl: SetContextStateAction<{ origin: string }>;
}>({
  currentUrl: {},
  updateCurrentUrl: () => {},
});

export const CurrentSessionContext = createContext<{
  currentSession: any;
  updateCurrentSession: SetContextStateAction<any>;
}>({
  currentSession: {},
  updateCurrentSession: () => {},
});

export const CurrentNetworkProvider = ({
  children,
  currentNetwork: initialCurrentNetwork = {} as Network,
  currentNetworkId: initialCurrentNetworkId,
}: {
  children: ReactNode;
  currentNetwork?: Network;
  currentNetworkId?: NetworkId;
}) => {
  const [currentNetwork, setCurrentNetwork] = useState(
    () => initialCurrentNetwork,
  );

  const [currentNetworkId, setCurrentNetworkId] = useState(
    initialCurrentNetwork?.id ?? initialCurrentNetworkId,
  );

  const value = useMemo(
    () => ({
      currentNetwork,
      currentNetworkId,
      updateCurrentNetwork: (network) => {
        setCurrentNetwork((prev) => ({ ...prev, ...network }));
        setCurrentNetworkId(network?.id);
      },
    }),
    [currentNetwork, currentNetworkId],
  );

  if (
    initialCurrentNetwork?.id &&
    initialCurrentNetworkId &&
    initialCurrentNetwork.id !== initialCurrentNetworkId
  ) {
    throw new Error('currentNetwork and currentNetworkId are not the same');
  }

  return (
    <CurrentNetworkContext.Provider value={value}>
      {children}
    </CurrentNetworkContext.Provider>
  );
};

interface CurrentUserProviderProps {
  children: ReactNode;
  currentUser: User | null;
}

export const CurrentUserProvider = ({
  children,
  currentUser: initialCurrentUser,
}: CurrentUserProviderProps) => {
  const [currentUser, setCurrentUser] = useState(() => initialCurrentUser);

  const value = useMemo(
    () => ({
      currentUser,
      updateCurrentUser: (user) =>
        setCurrentUser((prev) => ({ ...prev, ...user })),
    }),
    [currentUser],
  );

  return (
    <CurrentUserContext.Provider value={value}>
      {children}
    </CurrentUserContext.Provider>
  );
};

interface CurrentAdminProviderProps {
  children: ReactNode;
  currentAdmin: Admin | null;
}

export const CurrentAdminProvider = ({
  children,
  currentAdmin: initialCurrentAdmin,
}: CurrentAdminProviderProps) => {
  const [currentAdmin, setCurrentAdmin] = useState(() => initialCurrentAdmin);

  const value = useMemo(
    () => ({
      currentAdmin,
      updateCurrentAdmin: (admin) =>
        setCurrentAdmin((prev) => ({ ...prev, ...admin })),
    }),
    [currentAdmin],
  );

  return (
    <CurrentAdminContext.Provider value={value}>
      {children}
    </CurrentAdminContext.Provider>
  );
};

interface CurrentUrlProviderProps {
  children: ReactNode;
  currentUrl: Partial<{ origin: string }>;
}

export const CurrentUrlProvider = ({
  children,
  currentUrl: initialCurrentUrl,
}: CurrentUrlProviderProps) => {
  const [currentUrl, setCurrentUrl] = useState(() => initialCurrentUrl);

  const value = useMemo(
    () => ({
      currentUrl,
      updateCurrentUrl: (url) => setCurrentUrl(url),
    }),
    [currentUrl],
  );

  return (
    <CurrentUrlContext.Provider value={value}>
      {children}
    </CurrentUrlContext.Provider>
  );
};

interface CurrentSessionProviderProps {
  children: ReactNode;
  currentSession: {
    check_admin_dual_account: boolean;
    display_linkedin_import: boolean;
    has_linkedin_oauth2_token: boolean;
    linkedin_oauth2_token_expires_at: number;
    network_admin: boolean;
    user_can_target_users_from_messaging: boolean;
  };
}

export const CurrentSessionProvider = ({
  children,
  currentSession: {
    has_linkedin_oauth2_token: hasLinkedinOauth2Token,
    linkedin_oauth2_token_expires_at: linkedinOauth2TokenExpiresAt,
    check_admin_dual_account: checkAdminDualAccount,
    network_admin: networkAdmin,
    display_linkedin_import: displayLinkedinImport,
    user_can_target_users_from_messaging: userCanTargetUsersFromMessaging,
  },
}: CurrentSessionProviderProps) => {
  const [currentSession, setCurrentSession] = useState({
    hasLinkedinOauth2Token,
    linkedinOauth2TokenExpiresAt,
    checkAdminDualAccount,
    networkAdmin,
    displayLinkedinImport,
    userCanTargetUsersFromMessaging,
  });

  const value = useMemo(
    () => ({
      currentSession,
      updateCurrentSession: (session) => setCurrentSession(session),
    }),
    [currentSession],
  );

  return (
    <CurrentSessionContext.Provider value={value}>
      {children}
    </CurrentSessionContext.Provider>
  );
};

interface DefaultAppContextsProps {
  children: ReactNode;
  currentAdmin?: Admin | null;
  currentNetwork?: Network;
  currentNetworkId?: number;
  currentSession?: any;
  currentUrl?: Partial<{ origin: string }>;
  currentUser?: User | null;
}

const Provider = ({
  children,
  currentNetwork,
  currentNetworkId,
  currentUser = null,
  currentAdmin = null,
  currentUrl = getCurrentUrl(),
  currentSession = {},
}: DefaultAppContextsProps) => (
  <CurrentNetworkProvider
    currentNetwork={currentNetwork}
    currentNetworkId={currentNetworkId}
  >
    <CurrentUrlProvider currentUrl={currentUrl}>
      <CurrentAdminProvider currentAdmin={currentAdmin}>
        <CurrentUserProvider currentUser={currentUser}>
          <CurrentSessionProvider currentSession={currentSession}>
            {children}
          </CurrentSessionProvider>
        </CurrentUserProvider>
      </CurrentAdminProvider>
    </CurrentUrlProvider>
  </CurrentNetworkProvider>
);

export default { Provider };
